import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleBestHomemadeBreadVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "best-homemade-bread-baguettes-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || { articles: [] };
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Top Bakeries in Vancouver",
    "section-2": "How to Make the Best Homemade Bread & Baguettes",
    "local-regulations": "Local Regulations / Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  // Only references from the conversation (no external new references added)
  const referencesData = [
    {
      url: "https://www.bccdc.ca/health-info/food-your-health/temporary-food-markets",
      label: "BC Centre for Disease Control - Temporary Food Markets",
    },
    {
      url: "https://www.foodsafe.ca",
      label: "FOODSAFE & MarketSafe Courses",
    },
    {
      url: "https://www.kingarthurbaking.com/learn/guides/bread-baking",
      label: "King Arthur Baking - Bread Baking Guide",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // This matches the coordinates mentioned in the conversation for 10 bakeries
  // and references for the homemade bread steps if needed. Each location is
  // assigned an id that we will link to the relevant section text.
  const locations = [
    {
      id: "plaisir-sucre",
      lat: 49.2630,
      lng: -123.1530,
      popupText: "Plaisir Sucré (Kitsilano)",
    },
    {
      id: "ca-croustille",
      lat: 49.2642,
      lng: -123.1780,
      popupText: "Ça Croustille (Kitsilano)",
    },
    {
      id: "terra-breads",
      lat: 49.2690,
      lng: -123.1099,
      popupText: "Terra Breads (Olympic Village)",
    },
    {
      id: "purebread",
      lat: 49.2825,
      lng: -123.1087,
      popupText: "Purebread (Gastown)",
    },
    {
      id: "small-victory",
      lat: 49.2760,
      lng: -123.1210,
      popupText: "Small Victory (Yaletown)",
    },
    {
      id: "nelson-the-seagull",
      lat: 49.2830,
      lng: -123.1040,
      popupText: "Nelson the Seagull (Gastown)",
    },
    {
      id: "livia-forno-e-vino",
      lat: 49.2710,
      lng: -123.0690,
      popupText: "Livia Forno e Vino (Commercial Drive)",
    },
    {
      id: "flourist",
      lat: 49.2570,
      lng: -123.0690,
      popupText: "Flourist (East Vancouver)",
    },
    {
      id: "batard-boulangerie",
      lat: 49.2485,
      lng: -123.0900,
      popupText: "Bâtard Boulangerie (Fraserhood)",
    },
    {
      id: "a-bread-affair",
      lat: 49.2715,
      lng: -123.1340,
      popupText: "A Bread Affair (Granville Island)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Ultimate Guide to the Best Homemade Bread & Baguettes in Vancouver</title>
        <meta
          name="description"
          content="Discover Vancouver's top bakeries for artisanal bread and baguettes, and learn how to bake your own at home with practical tips, local guidelines, and FAQs."
        />
        <meta
          name="keywords"
          content="Vancouver bakeries, homemade bread, baguettes, bread-baking, local regulations, FAQ"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/best-homemade-bread-baguettes-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Ultimate Guide to the Best Homemade Bread & Baguettes in Vancouver"
        />
        <meta
          property="og:description"
          content="Discover Vancouver's top bakeries for artisanal bread and baguettes, and learn how to bake your own at home with practical tips, local guidelines, and FAQs."
        />
        <meta
          property="og:image"
          content="https://www.bcouver.com/assets/images/best-homemade-bread-baguettes-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/best-homemade-bread-baguettes-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Ultimate Guide to the Best Homemade Bread & Baguettes in Vancouver"
        />
        <meta
          name="twitter:description"
          content="Discover Vancouver's top bakeries for artisanal bread and baguettes, and learn how to bake your own at home with practical tips, local guidelines, and FAQs."
        />
        <meta
          name="twitter:image"
          content="https://www.bcouver.com/assets/images/best-homemade-bread-baguettes-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="A variety of fresh bread loaves and baguettes"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              There’s something extra special about biting into a loaf of bread
              that’s been made with care – the crackle of a baguette’s crust and
              the soft chew of its crumb are unforgettable. In Vancouver, a city
              renowned for its bustling food scene, high-quality bread and
              baguettes have become a delicious symbol of local craftsmanship.
              Baking your own at home can be equally rewarding. This guide
              showcases Vancouver’s best bakeries for artisanal bread and
              baguettes, offers step-by-step tips for home baking, clarifies
              local regulations for selling baked goods, and answers your most
              common bread questions.
            </p>

            {/* SECTION 1: Top Bakeries in Vancouver */}
            <h2 id="section-1">Top Bakeries in Vancouver</h2>
            <p>
              Vancouver is rich with talented bakers and cherished boulangeries
              specializing in hearty sourdough, crusty baguettes, and more.
              Below are ten standout spots, each with its own appeal, whether
              you’re a connoisseur seeking a perfect French baguette or a
              newcomer hunting for a traditional sourdough. Check out the
              coordinates to better visualize where each gem is located in the
              city.
            </p>

            <h3 ref={(el) => setSpotRef("plaisir-sucre", el)} style={{ scrollMarginTop: "80px" }}>
              1. Plaisir Sucré (Kitsilano){" "}
              <Marker
                coordY={49.2630}
                coordX={-123.1530}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h3>
            <p>
              <strong>Address:</strong> 2668 Arbutus St, Vancouver, BC V6J 5L8
              <br />
              <strong>Highlights:</strong> Authentic French boulangerie known
              for its traditional baguettes, croissants, and pastries. A
              family-run bakery that often has lines of locals waiting for fresh
              baked goods.
              <br />
              <strong>Price Range:</strong> $
              <br />
              <strong>Hours:</strong> Tue–Sat 8:00am–6:00pm; Sun 8:00am–5:00pm
              (Closed Mondays)
            </p>

            <h3 ref={(el) => setSpotRef("ca-croustille", el)} style={{ scrollMarginTop: "80px" }}>
              2. Ça Croustille (Kitsilano){" "}
              <Marker
                coordY={49.2642}
                coordX={-123.1780}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h3>
            <p>
              <strong>Address:</strong> 3273 W Broadway, Vancouver, BC V6K 2H5
              <br />
              <strong>Highlights:</strong> A charming French bakery that began
              at farmers’ markets, loved for its textbook baguettes with a crisp
              crust and tender crumb.
              <br />
              <strong>Price Range:</strong> $
              <br />
              <strong>Hours:</strong> Wed–Fri 7:00am–7:00pm; Sat–Sun
              7:00am–5:00pm; Closed Mon & Tue
            </p>

            <h3 ref={(el) => setSpotRef("terra-breads", el)} style={{ scrollMarginTop: "80px" }}>
              3. Terra Breads (Olympic Village){" "}
              <Marker
                coordY={49.2690}
                coordX={-123.1099}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h3>
            <p>
              <strong>Address:</strong> 1605 Manitoba St, Vancouver, BC V5Y 0B8
              <br />
              <strong>Highlights:</strong> A pioneer in Vancouver’s artisan
              bread movement using slow fermentation. Known for sourdoughs with
              unique flavors like cranberry walnut, plus a classic baguette
              that’s crusty outside, soft inside.
              <br />
              <strong>Price Range:</strong> $$
              <br />
              <strong>Hours:</strong> Mon–Sun 7:30am–5:00pm
            </p>

            <h3 ref={(el) => setSpotRef("purebread", el)} style={{ scrollMarginTop: "80px" }}>
              4. Purebread (Gastown){" "}
              <Marker
                coordY={49.2825}
                coordX={-123.1087}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h3>
            <p>
              <strong>Address:</strong> 159 W Hastings St, Vancouver, BC V6B 1H5
              <br />
              <strong>Highlights:</strong> Huge selection of breads and baked
              goods displayed artfully. Their sourdough baguette and ciabatta
              are big hits. Originated in Whistler, now a Vancouver staple.
              <br />
              <strong>Price Range:</strong> $–$$
              <br />
              <strong>Hours:</strong> Daily 8:00am–4:00pm
            </p>

            <h3 ref={(el) => setSpotRef("small-victory", el)} style={{ scrollMarginTop: "80px" }}>
              5. Small Victory (Yaletown){" "}
              <Marker
                coordY={49.2760}
                coordX={-123.1210}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h3>
            <p>
              <strong>Address:</strong> 1088 Homer St, Vancouver, BC V6B 2W9
              <br />
              <strong>Highlights:</strong> A sleek café-bakery milling its own
              flour. Popular for sourdough loaves, baguettes, and croissants.
              Has multiple branches with the same high-quality offerings.
              <br />
              <strong>Price Range:</strong> $$
              <br />
              <strong>Hours:</strong> Mon–Fri 7:30am–6:30pm; Sat 8:00am–6:30pm;
              Sun 8:30am–6:30pm
            </p>

            <h3 ref={(el) => setSpotRef("nelson-the-seagull", el)} style={{ scrollMarginTop: "80px" }}>
              6. Nelson the Seagull (Gastown){" "}
              <Marker
                coordY={49.2830}
                coordX={-123.1040}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h3>
            <p>
              <strong>Address:</strong> 315 Carrall St, Vancouver, BC V6A 0A7
              <br />
              <strong>Highlights:</strong> A hip café-bakehouse specializing in
              two-day sourdough fermentation. Known for delicious avocado toast
              on their signature sourdough.
              <br />
              <strong>Price Range:</strong> $
              <br />
              <strong>Hours:</strong> ~8:00am–4:00pm daily
            </p>

            <h3 ref={(el) => setSpotRef("livia-forno-e-vino", el)} style={{ scrollMarginTop: "80px" }}>
              7. Livia Forno e Vino (Commercial Drive){" "}
              <Marker
                coordY={49.2710}
                coordX={-123.0690}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h3>
            <p>
              <strong>Address:</strong> 1399 Commercial Dr, Vancouver, BC V5L 3X5
              <br />
              <strong>Highlights:</strong> Part Italian bakery, part café/wine
              bar. Offers baguettes, Italian pane, focaccia, and cornetti. Bread
              is central to their menu (try their sandwiches or brunch
              tartines).
              <br />
              <strong>Price Range:</strong> $
              <br />
              <strong>Hours:</strong> Tue–Sun 9:00am–5:00pm (Closed Mondays)
            </p>

            <h3 ref={(el) => setSpotRef("flourist", el)} style={{ scrollMarginTop: "80px" }}>
              8. Flourist (East Vancouver){" "}
              <Marker
                coordY={49.2570}
                coordX={-123.0690}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h3>
            <p>
              <strong>Address:</strong> 3433 Commercial St, Vancouver, BC V5N 4E8
              <br />
              <strong>Highlights:</strong> Mills its own organic flour on-site.
              Offers heritage grain loaves, baguettes, and sourdough with a
              nutty depth. Hosts pizza nights with sourdough crusts.
              <br />
              <strong>Price Range:</strong> $
              <br />
              <strong>Hours:</strong> Daily 7:00am–6:00pm (open later Thu–Sat)
            </p>

            <h3 ref={(el) => setSpotRef("batard-boulangerie", el)} style={{ scrollMarginTop: "80px" }}>
              9. Bâtard Boulangerie (Fraserhood){" "}
              <Marker
                coordY={49.2485}
                coordX={-123.0900}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h3>
            <p>
              <strong>Address:</strong> 3958 Fraser St, Vancouver, BC V5V 4E4
              <br />
              <strong>Highlights:</strong> Family-run French café known for its
              baguette moderne, campagne sourdough, and viennoiseries.
              <br />
              <strong>Price Range:</strong> $
              <br />
              <strong>Hours:</strong> Wed–Sun 8:00am–4:00pm; Closed Mon & Tue
            </p>

            <h3 ref={(el) => setSpotRef("a-bread-affair", el)} style={{ scrollMarginTop: "80px" }}>
              10. A Bread Affair (Granville Island){" "}
              <Marker
                coordY={49.2715}
                coordX={-123.1340}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h3>
            <p>
              <strong>Address:</strong> 1680 Johnston St, Vancouver, BC V6H 3S2
              <br />
              <strong>Highlights:</strong> BC’s first certified organic bakery,
              specializing in farm-to-table style bread. Offers classic
              baguettes, sourdough, and inventive loaves like apple cheddar.
              Located in bustling Granville Island.
              <br />
              <strong>Price Range:</strong> $
              <br />
              <strong>Hours:</strong> 7 days a week, ~8:00am–7:00pm (slightly
              shorter hours in winter)
            </p>

            {/* SECTION 2: How to Make the Best Homemade Bread & Baguettes */}
            <h2 id="section-2">How to Make the Best Homemade Bread &amp; Baguettes</h2>
            <p>
              Baking bread at home can be incredibly satisfying. Below is a
              concise overview of the essential steps – all drawn from the tips
              provided in our conversation above.
            </p>
            <ol>
              <li><strong>Gather Quality Ingredients:</strong> Use flour, water, yeast, and salt. Quality flour (11–12% protein) is ideal for baguettes.</li>
              <li><strong>Mix &amp; Knead the Dough:</strong> Combine ingredients into a shaggy dough, then knead or use stretch-and-fold methods.</li>
              <li><strong>First Rise (Bulk Fermentation):</strong> Let dough rise until doubled. For deeper flavor, refrigerate overnight with less yeast.</li>
              <li><strong>Shape the Loaves:</strong> Gently form baguettes or boules, taking care not to deflate the dough too much.</li>
              <li><strong>Proof (Second Rise):</strong> Let shaped dough rise until about 50–70% larger.</li>
              <li><strong>Score &amp; Bake with Steam:</strong> Slash the top, then use steam (e.g., tray of hot water) or a Dutch oven to get a crispy crust.</li>
              <li><strong>Cool &amp; Enjoy:</strong> Allow bread to cool before slicing for the best texture and flavor.</li>
            </ol>
            <p>
              Each of these steps contributes to the final texture and flavor of
              your bread or baguette. Don’t hesitate to experiment – bread
              baking is both art and science. Practice and a good kitchen scale
              can help you master these techniques.
            </p>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations / Best Practices</h2>
            <p>
              If you plan to sell your homemade bread, keep in mind Vancouver
              Coastal Health guidelines. Low-risk foods like bread can be sold
              at farmers’ markets if you follow <em>temporary food market</em>{" "}
              rules. Most commercial sales require a certified kitchen, proper
              labeling, and a business license. Consider taking MarketSafe or
              FOODSAFE Level 1 for essential food safety knowledge. As always,
              check with local authorities for the most up-to-date requirements.
              For purely home consumption, follow basic hygiene and ensure
              surfaces and tools are sanitized.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Where can I find the most authentic French baguette in Vancouver?</h3>
              <p>
                Plaisir Sucré, Ça Croustille, and Bâtard Boulangerie are
                excellent bets for traditional French-style baguettes, each run
                or staffed by French-trained bakers who uphold classic
                techniques.
              </p>

              <h3>2. Can I sell homemade bread in Vancouver?</h3>
              <p>
                Yes, under certain rules. You can typically sell low-risk baked
                goods like bread at farmers’ markets after meeting Vancouver
                Coastal Health and BC Centre for Disease Control guidelines.
                Most other commercial sales require an approved facility and
                business licensing.
              </p>

              <h3>3. How do I keep bread from going stale too quickly?</h3>
              <p>
                Store it at room temperature in a bread box or wrapped in
                breathable material (not the refrigerator). Freeze any bread you
                won’t finish within a couple of days. If your baguette loses its
                crisp crust, lightly mist with water and reheat in the oven for
                a few minutes.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s bread scene offers plenty of fantastic options,
              whether you prefer to buy from local artisans or craft your own at
              home. Each bakery listed here has a unique twist on classic
              French-style baguettes or rustic sourdough. If you’re feeling
              ambitious, try your hand at homemade loaves – with practice,
              you’ll be delighting friends and family with warm, crusty bread
              straight from your oven. Above all, enjoy the process: discover
              new flavors, support local bakers, and share your bread bounty.
              Life’s too short to eat mediocre bread!
            </p>
            <p>
              <strong>Disclaimer:</strong> This guide is for information only.
              Always follow proper food safety practices and confirm regulations
              with Vancouver Coastal Health if planning to sell baked goods.
              Individual baking results may vary. Those with dietary restrictions
              should consult bakeries for allergen information or use
              gluten-free recipes at home.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}